import { useMemo } from "react";

import { type ChartTableSelection, SHARED_TABLES_CRITERIA } from "../../lib/chartTables.js";
import { tableColorFromName } from "../../lib/gamePalette.js";
import { useSharedChartTables } from "../../lib/useSharedChartTables.js";
import type { FilterState, HealthData, OpenRawEventsHandler } from "../../types.js";
import { ChartCard } from "../ChartCard.js";
import { PieChart } from "../charts/PieChart.js";

const CRITERIA = `Source: device_events where event_name in ('client_error','console_warning','console_error') — same set and severity as Errors / Warnings by Day and Top Issues.

Severity:
• warning if client_error with properties.log_level warning, or console_warning
• error otherwise

Each slice: one table (label **table · venue**), value = sum of **error** counts over the filter window. Warnings are not included. Tables with zero errors are omitted.

Colours: table palette in \`gamePalette.ts\` (\`tableColorFromName\` — same idea as game colours: optional overrides + golden-angle hues so tables look distinct). Key left, pie right (default PieChart layout).

${SHARED_TABLES_CRITERIA}

Slice click: Raw Events → event_name client_error, error bucket, and the dashboard Device filter set to that table. console_error rows are counted in the slice but Raw Events can only filter one event name, so they are not included in that drill-down.`;

interface ErrorsPerTableCardProps {
  devices: HealthData["devices"];
  issuesByDeviceDay: HealthData["issuesByDeviceDay"];
  chartTables: ChartTableSelection;
  onChartTablesChange: (next: ChartTableSelection) => void;
  filters: FilterState;
  onOpenRawEvents: OpenRawEventsHandler;
}

export function ErrorsPerTableCard({
  devices,
  issuesByDeviceDay,
  chartTables,
  onChartTablesChange,
  filters,
  onOpenRawEvents,
}: ErrorsPerTableCardProps) {
  const tables = useSharedChartTables(devices, chartTables, onChartTablesChange, filters);

  const slices = useMemo(() => {
    const byDevice = new Map<string, number>();
    for (const row of issuesByDeviceDay) {
      if (!tables.effectiveIds.has(row.deviceId)) continue;
      byDevice.set(row.deviceId, (byDevice.get(row.deviceId) ?? 0) + row.errorCount);
    }
    return devices
      .filter((device) => device.name.trim() && (byDevice.get(device.id) ?? 0) > 0)
      .map((device) => {
        const venue = (device.venueName ?? "").trim();
        const name = device.name.trim();
        const label = venue ? `${name} · ${venue}` : name;
        return {
          deviceId: device.id,
          label,
          pieLabel: name,
          value: byDevice.get(device.id) ?? 0,
          color: tableColorFromName(label),
        };
      })
      .sort((a, b) => b.value - a.value || a.label.localeCompare(b.label));
  }, [devices, issuesByDeviceDay, tables.effectiveIds]);

  const total = slices.reduce((sum, slice) => sum + slice.value, 0);

  return (
    <ChartCard
      title="Errors per table"
      criteria={CRITERIA}
      note="Error severity only (not warnings). Key top-left; pie fills the right half. Slice colours use the table palette (like games) so each table reads as a different hue."
      titleRight={
        <span className="shrink-0 text-[12px] font-medium tabular-nums text-text-secondary">
          {total} errors
        </span>
      }
    >
      <PieChart
        slices={slices}
        formatValue={(value) => String(Math.round(value))}
        sliceTooltip={(slice, i) => {
          const row = slices[i];
          const pct = total > 0 ? Math.round((slice.value / total) * 100) : 0;
          const name = row?.label ?? slice.label;
          return `${name}: ${Math.round(slice.value)} errors (${pct}% of errors). Click: Raw Events → client_error (error) on this table. console_error is counted here but not in that event filter. Middle-click / Ctrl/Cmd+click: new tab.`;
        }}
        onSliceClick={(_slice, i, e) => {
          const row = slices[i];
          if (!row) return;
          onOpenRawEvents(
            {
              eventName: "client_error",
              clientErrorBucket: "error",
              deviceId: row.deviceId,
            },
            e,
          );
        }}
      />
    </ChartCard>
  );
}
